import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, MessageCircle, Download, MapPin, Share2 } from "lucide-react";
import { toast } from "sonner";
import ServicePrice from "../components/ServicePrice";
import { useCatalog } from "../contexts/CatalogContext";
import { API } from "../lib/api";

export default function BookingSuccess() {
  const { id } = useParams();
  const { state } = useLocation();
  const [booking, setBooking] = useState(state?.booking || null);
  const [loading, setLoading] = useState(!state?.booking);

  const { getCategoryById, getServiceById, CITIES, SLOTS, CONTACT } = useCatalog();

  useEffect(() => {
    if (booking) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/bookings/${id}`);
        setBooking(res.data);
      } catch (e) {
        toast.error("Couldn't load booking details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, booking]);

  if (loading) return <div className="max-w-xl mx-auto py-20 text-center text-slate-500">Loading…</div>;
  if (!booking) return <div className="max-w-xl mx-auto py-20 text-center text-slate-500">Booking not found.</div>;

  const cat = getCategoryById(booking.category);
  const svc = getServiceById(booking.category, booking.service);
  const slotLbl = SLOTS.find((s) => s.id === booking.slot)?.label || booking.slot;
  const cityLbl = CITIES.find((c) => c.id === booking.city)?.name || booking.city;

  const waMessage = encodeURIComponent(
    `Hi Repairro! I just booked a service.%0ABooking ID: ${booking.booking_id}%0AService: ${booking.service_name}%0ADate: ${booking.date} • ${slotLbl}`
  );
  const waShareUrl = `https://wa.me/?text=${waMessage}`;

  const downloadReceipt = () => {
    const lines = [
      "REPAIRRO — Booking Receipt",
      "Service. Technology. Trust.",
      "------------------------------",
      `Booking ID: ${booking.booking_id}`,
      `Status: ${booking.status}`,
      `Created: ${new Date(booking.created_at).toLocaleString("en-IN")}`,
      "",
      `Service: ${booking.service_name}`,
      `Brand: ${booking.brand}`,
      `Estimated charge: ₹${booking.price}`,
      "",
      `Customer: ${booking.name}`,
      `Phone: ${booking.phone}`,
      booking.email ? `Email: ${booking.email}` : "",
      "",
      `Address: ${booking.address}, ${cityLbl}${booking.pincode ? " - " + booking.pincode : ""}`,
      `Date & Slot: ${booking.date} • ${slotLbl}`,
      "",
      `Support: ${CONTACT.phone} / ${CONTACT.email}`,
    ].filter(Boolean).join("\n");
    const blob = new Blob([lines], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Repairro-${booking.booking_id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="booking-success-page">
      <div className="bg-white rounded-2xl border border-slate-200 card-shadow p-8 sm:p-10 text-center">
        <div className="w-20 h-20 mx-auto rounded-full bg-[#22C55E]/10 flex items-center justify-center">
          <CheckCircle2 className="w-12 h-12 text-[#22C55E]" />
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 mt-5" data-testid="success-heading">
          Booking Confirmed!
        </h1>
        <p className="text-slate-600 mt-2">Our technician will reach out before the slot to confirm timing.</p>

        <div className="mt-6 inline-block bg-[#0C4A6E] text-white rounded-xl px-6 py-3" data-testid="success-booking-id">
          <span className="text-xs font-semibold opacity-80 block">Booking ID</span>
          <span className="font-display font-extrabold text-xl tracking-wider">#{booking.booking_id}</span>
        </div>

        <div className="mt-8 text-left bg-slate-50 rounded-xl p-5 space-y-2 text-sm" data-testid="success-summary">
          <Row k="Service" v={`${booking.service_name} • ${booking.brand}`} />
          <Row k="Date & Slot" v={`${booking.date} • ${slotLbl}`} />
          <Row k="Address" v={`${booking.address}, ${cityLbl}${booking.pincode ? " - " + booking.pincode : ""}`} />
          <Row k="Estimated charge" v={<ServicePrice service={booking} />} />
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <a
            href={waShareUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="success-share-whatsapp"
            className="bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition-all"
          >
            <MessageCircle className="w-5 h-5" /> Share on WhatsApp
          </a>
          <button
            onClick={downloadReceipt}
            data-testid="success-download-receipt"
            className="bg-[#0C4A6E] hover:bg-[#132D53] text-white font-semibold px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition-all"
          >
            <Download className="w-5 h-5" /> Download Receipt
          </button>
          <Link
            to={`/booking/track/${booking.booking_id}`}
            data-testid="success-track-booking"
            className="bg-white border border-slate-200 hover:border-[#0C4A6E] text-slate-800 font-semibold px-4 py-3 rounded-xl flex items-center justify-center gap-2 transition-all"
          >
            <MapPin className="w-5 h-5" /> Track Booking
          </Link>
        </div>

        <p className="text-xs text-slate-500 mt-8">
          Need to change something? WhatsApp us at{" "}
          <a href={CONTACT.whatsappLink} target="_blank" rel="noreferrer" className="text-[#0369A1] font-semibold">
            {CONTACT.phone}
          </a>{" "}
          — no charges to reschedule.
        </p>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-slate-500">{k}</span>
    <span className="font-semibold text-slate-900 text-right">{v}</span>
  </div>
);
